import { useEffect, useState, useRef, useCallback } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  Compass,
  ExternalLink,
  Flame,
  Footprints,
  KeyRound,
  MapPin,
  Music2,
  Play,
  RefreshCw,
  Sparkles,
  Utensils,
  Coffee as CoffeeIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

/* ============================================================
   EDITABLE CONTENT — change anything below without touching JSX
   ============================================================ */

const HOUSE = {
  name: 'Amelia Payson House',
  address: '16 Winter Street',
  city: 'Salem, Massachusetts',
  websiteUrl: 'https://www.ameliapaysonhouse.com',
  instagramUrl: 'https://www.instagram.com/ameliapaysonhouse',
  googleMapsUrl: 'https://www.google.com/maps/search/?api=1&query=Amelia+Payson+House+16+Winter+Street+Salem+MA',
  heroImage: '/IMG_7497.jpeg',
  storyImage: '/amelia-story.webp',
};

const BOND = {
  name: 'Bond Coffee Roasters',
  websiteUrl: 'https://www.thebondcoffee.com',
};

const SPOTIFY_URL = 'https://open.spotify.com/playlist/5kdEXyDTcq0SWYyOsu5HSC?si=k2k5Uw90ThqMe54WuapOkw&utm_source=copy-link&pi=SJCavshaRTaax';

const BUY_BAG = {
  size: '12oz',
  price: '$27',
  stripeUrl: 'https://buy.stripe.com/28EdRa6TB5ST83X97Q3VC0x',
};

const PLAYLIST = {
  heading: 'A Slow Morning on Winter Street',
  subheadline: 'Five songs for coffee, old houses, and wandering Salem without much of a plan.',
  closingLine: 'Not a witch song in sight.',
  tracks: [
    { num: '01', title: 'Northern Sky', artist: 'Nick Drake', blurb: 'Quiet, elegant, old-New-England-morning energy.' },
    { num: '02', title: 'Autumn Sweater', artist: 'Yo La Tengo', blurb: 'Fall without being obvious or cheesy.' },
    { num: '03', title: 'The Stable Song', artist: 'Gregory Alan Isakov', blurb: 'Warm, historic-house, coffee-by-the-fireplace feel.' },
    { num: '04', title: 'Old Pine', artist: 'Ben Howard', blurb: 'Coastal New England, wandering-around-town energy.' },
    { num: '05', title: 'Salem', artist: 'Bon Iver', blurb: 'The local nod, but still tasteful and atmospheric.' },
  ],
};

const HOUSE_HISTORY = {
  intro:
    'Edward H. Payson was born in Roxbury on December 5, 1803 and came to Salem as a boy.\n\nHe began his business career at Mr. Fenno\u2019s brokerage office and, at the age of 23, accepted a position at the Commercial Bank in Salem, where he worked as a cashier.\n\nOn September 29, 1829, Edward married Amelia Mellus of Dorchester.\n\nIn 1845, Edward and Amelia built their home at 16 Winter Street in Salem.\n\nThat home is now known as the Amelia Payson House.',
  closingLine: 'Not a bad run for a house that predates the light bulb.',
  timeline: [
    { year: '1803', text: 'Edward H. Payson is born in Roxbury.' },
    { year: '1829', text: 'Edward marries Amelia Mellus of Dorchester.' },
    { year: '1845', text: 'Edward and Amelia build their home at 16 Winter Street in Salem.' },
    { year: 'Today', text: 'You woke up here and scanned a coffee bag.' },
  ],
};

const EDWARD_CARD = {
  headline: 'Edward wasn\u2019t just a banker.',
  copy:
    'Historical records note that Edward Payson was active in Salem\u2019s early anti-slavery movement and helped support the construction of Salem\u2019s North Church in 1834.\n\nHe remained a familiar figure in Salem long after retiring from banking.',
};

const COFFEE = {
  intro:
    'This dark, rich espresso-style blend was created exclusively for the Amelia Payson House by Bond Coffee Roasters.\n\nIt brings together two coffees in a balanced 50/50 blend.',
  summaryHeadline: 'Dark. Rich. Balanced.',
  summaryCopy:
    'Roasted as a full-bodied espresso-style blend, but designed to be easy to drink whether you brew it strong, add milk, or simply want a big, comforting cup of coffee in the morning.\n\nThe Guatemalan coffee brings clarity and structure.\n\nThe Salvadoran coffee adds sweetness and depth.\n\nTogether, they make a coffee that feels right at home in an old Salem house on a slow morning.',
  tastingStrip: ['Cocoa', 'Caramel', 'Citrus', 'Brown Sugar', 'Bittersweet Chocolate'],
  coffees: [
    {
      name: 'Guatemala Antigua',
      pct: '50%',
      origin: 'From Finca Medina in the volcanic soils of Antigua.',
      desc:
        'This coffee gives the blend its smooth, balanced foundation, with citrus brightness, cocoa, caramel, nutty sweetness, red fruit, and a creamy texture.',
      notes: ['Orange', 'Mandarin', 'Lemon', 'Cocoa', 'Caramel', 'Nutty sweetness', 'Red fruit', 'Creamy finish'],
      nerd: ['Guatemala Antigua', 'Finca Medina', 'Washed process', '50%'],
    },
    {
      name: 'El Salvador Atiquizaya Tabi',
      pct: '50%',
      origin: 'From Finca Miravalles in Apaneca, Ahuachap\u00e1n, near the Santa Ana volcano.',
      desc:
        'The Tabi varietal brings sweetness, depth, and balance to the blend.',
      notes: ['Raw sugar', 'Caramel', 'Cocoa powder', 'Cinnamon-sugar toast', 'Roasted barley', 'Tea-like snap', 'Bittersweet chocolate'],
      nerd: ['El Salvador Atiquizaya Tabi', 'Finca Miravalles', 'Apaneca, Ahuachap\u00e1n', 'Tabi varietal', '50%'],
    },
  ],
};

const BREW_STEPS = [
  'Add the full 3oz pouch of ground coffee.',
  'Add 45oz (about 5½ cups) of fresh water — a 1:16 ratio.',
  'Brew.',
  'Ignore your phone for approximately five minutes.',
  'Drink somewhere comfortable.',
];

const BREW_NOTE = 'Coffee tastes approximately 14% better near a fireplace.\nWe have no scientific evidence for this.';

const HOUSE_CARDS = [
  { icon: 'flame', title: 'Fireplace Weather', desc: 'Every guest room has a gas fireplace.' },
  { icon: 'utensils', title: 'Breakfast Is Handled', desc: 'Gourmet breakfast options are included with every stay.' },
  { icon: 'coffee', title: 'The 3 p.m. Problem', desc: 'Afternoon tea and coffee come with complimentary homemade cookies.' },
  { icon: 'key', title: 'Old House. Good Sleep.', desc: 'Historic surroundings meet modern comforts.' },
];

const LOCAL_PICKS = [
  { name: 'Host Pick Coming Soon', category: 'EAT', note: 'A Salem favorite worth the walk. Details soon.', walk: '' },
  { name: 'Host Pick Coming Soon', category: 'DRINK', note: 'Where the host grabs a drink. Details soon.', walk: '' },
  { name: 'Host Pick Coming Soon', category: 'SEE', note: 'Something you should not miss. Details soon.', walk: '' },
  { name: 'Host Pick Coming Soon', category: 'WANDER', note: 'A street worth getting lost on. Details soon.', walk: '' },
  { name: 'Host Pick Coming Soon', category: 'SECRET', note: 'Ask the host in person. Details soon.', walk: '' },
];

const WALK_ATTRACTIONS = [
  { name: 'Salem Witch Museum', query: 'Salem Witch Museum, Salem, MA' },
  { name: 'Peabody Essex Museum', query: 'Peabody Essex Museum, Salem, MA' },
  { name: 'The Witch House', query: 'The Witch House, Salem, MA' },
  { name: 'Salem Common', query: 'Salem Common, Salem, MA' },
  { name: 'The House of the Seven Gables', query: 'The House of the Seven Gables, Salem, MA' },
  { name: 'Salem Maritime National Historic Site', query: 'Salem Maritime National Historic Site, Salem, MA' },
  { name: 'Salem Waterfront', query: 'Salem Waterfront, Salem, MA' },
];

const SALEM_MISSIONS = [
  'Find a Salem building older than the house you slept in.',
  'Walk one block you weren\u2019t planning to walk.',
  'Order something you\u2019ve never had before.',
  'Find the best front door in Salem.',
  'Take one photo you absolutely will not post.',
  'Ask a local where they would go if they had two free hours.',
  'Find the quietest spot you can within ten minutes of downtown.',
  'Find something in Salem that has absolutely nothing to do with witches.',
];

const AMENITIES = [
  'Beautifully renovated historic guest rooms',
  'Gas fireplaces in every room',
  'Gourmet breakfast included',
  'Afternoon tea and coffee',
  'Complimentary homemade cookies',
];

/* ============================================================
   END EDITABLE CONTENT
   ============================================================ */

const NAV_SECTIONS = [
  { id: 'story', label: 'The House' },
  { id: 'coffee', label: 'The Coffee' },
  { id: 'brew', label: 'Brew' },
  { id: 'picks', label: 'Local Picks' },
  { id: 'walk', label: 'Walk' },
  { id: 'mission', label: 'Mission' },
];

const iconMap: Record<string, React.ReactNode> = {
  flame: <Flame className="h-6 w-6" />,
  utensils: <Utensils className="h-6 w-6" />,
  coffee: <CoffeeIcon className="h-6 w-6" />,
  key: <KeyRound className="h-6 w-6" />,
};

const walkMapUrl = (query: string) =>
  `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent('16 Winter Street, Salem, MA')}&destination=${encodeURIComponent(query)}&travelmode=walking`;

const fullMapUrl = HOUSE.googleMapsUrl;

export default function AmeliaPaysonHouse() {
  const [showBackTop, setShowBackTop] = useState(false);
  const [mission, setMission] = useState('');
  const [nerdOpen, setNerdOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    document.title = 'Amelia Payson House \u00b7 Good Morning from 1845';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'A hidden digital companion to your stay at the Amelia Payson House in Salem. Coffee by Bond Coffee Roasters, local history, and a few things worth knowing.');

    setMission(SALEM_MISSIONS[Math.floor(Math.random() * SALEM_MISSIONS.length)]);

    const onScroll = () => setShowBackTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('amelia-in');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    document.querySelectorAll('.amelia-reveal').forEach((el) => observerRef.current?.observe(el));

    return () => {
      window.removeEventListener('scroll', onScroll);
      observerRef.current?.disconnect();
    };
  }, []);

  const newMission = useCallback(() => {
    let next = mission;
    while (next === mission) {
      next = SALEM_MISSIONS[Math.floor(Math.random() * SALEM_MISSIONS.length)];
    }
    setMission(next);
  }, [mission]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#f7f1e8] text-[#1a2238] selection:bg-[#c0623a] selection:text-white">
      {/* ── Top bar ── */}
      <header className="fixed left-0 right-0 top-0 z-40 border-b border-[#1a2238]/10 bg-[#f7f1e8]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3.5">
          <span className="font-serif text-lg tracking-tight text-[#1a2238]">{HOUSE.name}</span>
          <nav className="hidden items-center gap-5 sm:flex">
            {NAV_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#1a2238]/55 transition hover:text-[#c0623a]"
              >
                {s.label}
              </button>
            ))}
          </nav>
          <button
            onClick={() => scrollTo('top')}
            className="grid h-9 w-9 place-items-center rounded-full border border-[#1a2238]/20 text-[#1a2238]/70 transition hover:border-[#c0623a] hover:text-[#c0623a] sm:hidden"
            aria-label="Back to top"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main id="top" className="pt-14">
        {/* ── 1. HERO ── */}
        <section className="relative isolate flex min-h-[92svh] flex-col overflow-hidden">
          <img
            src={HOUSE.heroImage}
            alt="The Amelia Payson House on Winter Street in Salem"
            className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(247,241,232,.48)_0%,rgba(247,241,232,.16)_42%,rgba(247,241,232,.92)_88%,#f7f1e8_100%)]" />
          <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-end px-5 pb-16 pt-20">
            <div className="max-w-xl rounded-2xl border border-white/40 bg-[#f7f1e8]/85 p-7 shadow-[0_8px_30px_rgba(26,34,56,0.18)] backdrop-blur-md sm:p-8">
              <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.28em] text-[#1a2238]">
                {HOUSE.address} · {HOUSE.city}
              </p>
              <h1 className="font-serif text-5xl leading-[.92] tracking-[-0.01em] text-[#1a2238] drop-shadow-[0_1px_2px_rgba(247,241,232,0.6)] sm:text-6xl lg:text-7xl">
                Good morning<br />from 1845.
              </h1>
              <p className="mt-5 max-w-md text-base leading-relaxed text-[#1a2238]/85 sm:text-lg">
                You’re drinking a coffee created exclusively for the Amelia Payson House by Bond Coffee Roasters.
              </p>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-[#1a2238]/70">
                A little coffee. A little history. A few things worth knowing before you head out into Salem.
              </p>
              <div className="mt-8">
                <button
                  onClick={() => scrollTo('story')}
                  className="inline-flex items-center gap-2 rounded-full bg-[#c0623a] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#a8512f] hover:-translate-y-0.5"
                >
                  Start your morning <ArrowDown className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── 2. THE HOUSE HAS A STORY ── */}
        <section id="story" className="scroll-mt-20 bg-[#f7f1e8] py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-5">
            <div className="amelia-reveal">
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#c0623a]">The house has a story</p>
              <h2 className="mt-4 font-serif text-4xl leading-[.95] text-[#1a2238] sm:text-5xl">
                Before it was your room...
              </h2>
            </div>

            <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
              <div className="amelia-reveal">
                <img
                  src={HOUSE.storyImage}
                  alt="A historic view of the Amelia Payson House"
                  className="aspect-[4/5] w-full rounded-2xl object-cover shadow-lg"
                  loading="lazy"
                />
              </div>
              <div className="amelia-reveal">
                <div className="space-y-4 text-[15px] leading-relaxed text-[#1a2238]/75">
                  {HOUSE_HISTORY.intro.split('\n\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
                <div className="mt-10 space-y-0">
                  {HOUSE_HISTORY.timeline.map((item, i) => (
                    <div key={i} className="flex gap-5 border-l-2 border-[#c0623a]/25 pl-5 pb-6 last:pb-0">
                      <div className="-ml-[27px] mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 border-[#c0623a] bg-[#f7f1e8]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#c0623a]" />
                      </div>
                      <div>
                        <p className="font-serif text-xl text-[#c0623a]">{item.year}</p>
                        <p className="mt-1 text-sm leading-relaxed text-[#1a2238]/70">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-6 font-serif text-lg italic text-[#1a2238]/55">{HOUSE_HISTORY.closingLine}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. ONE MORE THING ABOUT EDWARD ── */}
        <section className="bg-[#efe7d8] py-16 lg:py-20">
          <div className="mx-auto max-w-5xl px-5">
            <div className="amelia-reveal rounded-2xl border border-[#1a2238]/12 bg-[#f7f1e8] p-8 shadow-sm sm:p-10">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-[#c0623a]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#c0623a]">Did you know?</span>
              </div>
              <h3 className="mt-5 font-serif text-3xl leading-tight text-[#1a2238]">{EDWARD_CARD.headline}</h3>
              <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-[#1a2238]/70">
                {EDWARD_CARD.copy.split('\n\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. YOUR COFFEE ── */}
        <section id="coffee" className="scroll-mt-20 bg-[#f7f1e8] py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-5">
            <div className="amelia-reveal">
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#c0623a]">Your coffee</p>
              <h2 className="mt-4 font-serif text-4xl leading-[.95] text-[#1a2238] sm:text-5xl">
                About what you’re drinking
              </h2>
              <div className="mt-5 max-w-2xl space-y-4 text-[15px] leading-relaxed text-[#1a2238]/70">
                {COFFEE.intro.split('\n\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </div>

            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {COFFEE.coffees.map((c, i) => (
                <article key={i} className="amelia-reveal rounded-2xl border border-[#1a2238]/12 bg-white/60 p-7 shadow-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-serif text-2xl text-[#1a2238]">{c.name}</h3>
                    <span className="font-serif text-2xl text-[#c0623a]">{c.pct}</span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-[#1a2238]/65">{c.origin}</p>
                  <p className="mt-3 text-sm leading-relaxed text-[#1a2238]/70">{c.desc}</p>
                  <div className="mt-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#c0623a]/70">Tasting notes</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {c.notes.map((n) => (
                        <span key={n} className="rounded-full border border-[#1a2238]/15 bg-[#efe7d8] px-3 py-1 text-xs font-medium text-[#1a2238]/75">
                          {n}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="amelia-reveal mt-10 rounded-2xl bg-[#1a2238] p-8 text-[#f7f1e8] sm:p-10">
              <h3 className="font-serif text-3xl tracking-tight text-[#c0623a]">{COFFEE.summaryHeadline}</h3>
              <div className="mt-5 max-w-2xl space-y-4 text-[15px] leading-relaxed text-[#f7f1e8]/75">
                {COFFEE.summaryCopy.split('\n\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </div>

            {/* Tasting strip */}
            <div className="amelia-reveal mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-y border-[#1a2238]/12 py-5">
              {COFFEE.tastingStrip.map((note, i) => (
                <span key={note} className="flex items-center gap-5 text-sm font-bold uppercase tracking-[0.14em] text-[#1a2238]/60">
                  {i > 0 && <span className="text-[#c0623a]/40">·</span>}
                  {note}
                </span>
              ))}
            </div>

            {/* Coffee nerds expandable */}
            <div className="amelia-reveal mt-6">
              <button
                onClick={() => setNerdOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-xl border border-[#1a2238]/12 bg-white/50 px-5 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#1a2238]/70 transition hover:border-[#c0623a]/40"
              >
                Coffee nerds, tap here
                <ChevronDown className={`h-4 w-4 transition-transform ${nerdOpen ? 'rotate-180' : ''}`} />
              </button>
              {nerdOpen && (
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  {COFFEE.coffees.map((c, i) => (
                    <div key={i} className="rounded-xl border border-[#1a2238]/10 bg-[#efe7d8] p-5">
                      {c.nerd.map((line, j) => (
                        <p key={j} className={`text-sm ${j === 0 ? 'font-serif text-base text-[#1a2238]' : 'text-[#1a2238]/65'}`}>
                          {line}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bond logo area */}
            <div className="amelia-reveal mt-10 flex flex-col items-center gap-4 text-center">
              <div className="text-center">
                <p className="font-serif text-4xl font-semibold tracking-[0.14em] text-[#1a2238]">BOND</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.32em] text-[#c0623a]">Coffee Roasters</p>
              </div>
              <p className="max-w-md text-sm leading-relaxed text-[#1a2238]/55">
                Roasted exclusively for the Amelia Payson House by Bond Coffee Roasters.
              </p>
              <a
                href={BOND.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#c0623a]/50 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#c0623a] transition hover:bg-[#c0623a] hover:text-white"
              >
                Meet Bond Coffee <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>

            {/* Buy this bag */}
            <div className="amelia-reveal mt-12 overflow-hidden rounded-2xl border border-[#1a2238]/12 bg-[#1a2238] text-[#f7f1e8]">
              <div className="flex flex-col items-center gap-5 px-6 py-8 text-center sm:flex-row sm:justify-between sm:px-10 sm:text-left">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#c0623a]">Like this coffee</p>
                  <h3 className="mt-2 font-serif text-2xl text-white sm:text-3xl">
                    Buy another bag
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#f7f1e8]/60">
                    {BUY_BAG.size} of the Amelia Payson House blend, roasted by Bond Coffee. {BUY_BAG.price} shipped.
                  </p>
                </div>
                <a
                  href={BUY_BAG.stripeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#c0623a] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#a8512f] hover:-translate-y-0.5"
                >
                  <CoffeeIcon className="h-4 w-4" /> Buy this bag — {BUY_BAG.price}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. HOW TO BREW IT ── */}
        <section id="brew" className="scroll-mt-20 bg-[#efe7d8] py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-5">
            <div className="amelia-reveal">
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#c0623a]">How to brew it</p>
              <h2 className="mt-4 font-serif text-4xl leading-[.95] text-[#1a2238] sm:text-5xl">
                Make the cup that was intended
              </h2>
            </div>
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {BREW_STEPS.map((step, i) => (
                <div key={i} className="amelia-reveal rounded-xl border border-[#1a2238]/10 bg-[#f7f1e8] p-5">
                  <span className="font-serif text-3xl text-[#c0623a]">{i + 1}</span>
                  <p className="mt-2 text-sm leading-relaxed text-[#1a2238]/70">{step}</p>
                </div>
              ))}
            </div>
            <div className="amelia-reveal mt-8 rounded-xl border border-[#c0623a]/20 bg-[#c0623a]/5 p-6 text-center">
              <p className="font-serif text-lg italic leading-relaxed text-[#1a2238]/65">
                {BREW_NOTE.split('\n').map((line, i) => (
                  <span key={i} className="block">{line}</span>
                ))}
              </p>
            </div>
          </div>
        </section>

        {/* ── 6. THE HOUSE, IN FOUR THINGS ── */}
        <section className="bg-[#f7f1e8] py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-5">
            <div className="amelia-reveal">
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#c0623a]">The house, in four things</p>
              <h2 className="mt-4 font-serif text-4xl leading-[.95] text-[#1a2238] sm:text-5xl">
                Four reasons you may not want to leave
              </h2>
            </div>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {HOUSE_CARDS.map((card, i) => (
                <article key={i} className="amelia-reveal rounded-2xl border border-[#1a2238]/12 bg-white/60 p-7 shadow-sm transition hover:-translate-y-1 hover:border-[#c0623a]/40">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-[#c0623a]/10 text-[#c0623a]">
                    {iconMap[card.icon]}
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-[#1a2238]">{card.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#1a2238]/65">{card.desc}</p>
                </article>
              ))}
            </div>
            <div className="amelia-reveal mt-10 text-center">
              <a
                href={HOUSE.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#1a2238]/25 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#1a2238]/70 transition hover:border-[#c0623a] hover:text-[#c0623a]"
              >
                See all house amenities <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* ── 7. LOCAL PICKS ── */}
        <section id="picks" className="scroll-mt-20 bg-[#efe7d8] py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-5">
            <div className="amelia-reveal">
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#c0623a]">Local picks</p>
              <h2 className="mt-4 font-serif text-4xl leading-[.95] text-[#1a2238] sm:text-5xl">
                Skip the algorithm.
              </h2>
              <p className="mt-4 max-w-md text-[15px] leading-relaxed text-[#1a2238]/65">
                A few Salem picks from someone who actually knows the place.
              </p>
            </div>
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {LOCAL_PICKS.map((pick, i) => (
                <article key={i} className="amelia-reveal rounded-2xl border border-[#1a2238]/12 bg-[#f7f1e8] p-6 shadow-sm">
                  <span className="rounded-full border border-[#c0623a]/40 px-3 py-1 text-[10px] font-bold tracking-[0.18em] text-[#c0623a]">
                    {pick.category}
                  </span>
                  <h3 className="mt-4 font-serif text-xl text-[#1a2238]">{pick.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#1a2238]/60">{pick.note}</p>
                  {pick.walk && (
                    <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-[#c0623a]/70">
                      <Footprints className="h-3.5 w-3.5" /> {pick.walk}
                    </p>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── 8. WALK FROM HERE ── */}
        <section id="walk" className="scroll-mt-20 bg-[#1a2238] py-20 lg:py-28 text-[#f7f1e8]">
          <div className="mx-auto max-w-5xl px-5">
            <div className="amelia-reveal">
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#c0623a]">Walk from here</p>
              <h2 className="mt-4 font-serif text-4xl leading-[.95] text-white sm:text-5xl">
                You’re closer than you think.
              </h2>
            </div>
            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {WALK_ATTRACTIONS.map((attr, i) => (
                <a
                  key={i}
                  href={walkMapUrl(attr.query)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="amelia-reveal group flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-5 py-4 transition hover:border-[#c0623a]/50 hover:bg-white/10"
                >
                  <div className="flex items-center gap-3">
                    <Compass className="h-5 w-5 shrink-0 text-[#c0623a]" />
                    <span className="font-serif text-base text-[#f7f1e8]/90 group-hover:text-white">{attr.name}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-[#f7f1e8]/40 transition group-hover:text-[#c0623a]" />
                </a>
              ))}
            </div>
            <div className="amelia-reveal mt-10">
              <a
                href={fullMapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#c0623a] px-6 py-3 text-sm font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#a8512f]"
              >
                Open full map <MapPin className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* ── 9. TODAY'S SALEM MISSION ── */}
        <section id="mission" className="scroll-mt-20 bg-[#c0623a] py-20 lg:py-28 text-white">
          <div className="mx-auto max-w-3xl px-5 text-center">
            <div className="amelia-reveal">
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-white/70">Today’s Salem mission</p>
              <h2 className="mt-4 font-serif text-3xl leading-[1.05] text-white sm:text-4xl">
                {mission}
              </h2>
              <button
                onClick={newMission}
                className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:border-white hover:bg-white/10"
              >
                Give me another <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>

        {/* ── 10. A SLOW MORNING ON WINTER STREET ── */}
        <section className="bg-[#efe7d8] py-20 lg:py-28">
          <div className="mx-auto max-w-2xl px-5">
            <div className="amelia-reveal text-center">
              <div className="mx-auto mb-5 flex items-center justify-center gap-2 text-[#c0623a]">
                <span className="h-px w-8 bg-[#c0623a]/30" />
                <Music2 className="h-4 w-4" />
                <span className="h-px w-8 bg-[#c0623a]/30" />
              </div>
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#c0623a]">The playlist</p>
              <h2 className="mt-4 font-serif text-4xl leading-[.95] text-[#1a2238] sm:text-5xl">
                {PLAYLIST.heading}
              </h2>
              <p className="mt-5 max-w-md mx-auto text-[15px] leading-relaxed text-[#1a2238]/65">
                {PLAYLIST.subheadline}
              </p>
            </div>

            <div className="amelia-reveal mt-12 divide-y divide-[#1a2238]/12 overflow-hidden rounded-2xl border border-[#1a2238]/12 bg-[#f7f1e8]">
              {PLAYLIST.tracks.map((track) => (
                <div key={track.num} className="flex items-center gap-5 px-6 py-5 transition hover:bg-[#c0623a]/5">
                  <span className="font-serif text-2xl text-[#c0623a]/50 tabular-nums">{track.num}</span>
                  <div className="h-8 w-px bg-[#1a2238]/10" />
                  <div className="flex-1">
                    <p className="font-serif text-lg text-[#1a2238]">{track.title}</p>
                    <p className="text-sm text-[#1a2238]/55">{track.artist}</p>
                    <p className="mt-1 text-[13px] italic leading-relaxed text-[#1a2238]/45">{track.blurb}</p>
                  </div>
                  <Music2 className="h-4 w-4 text-[#1a2238]/20" />
                </div>
              ))}
            </div>

            <p className="amelia-reveal mt-6 text-center font-serif text-base italic text-[#1a2238]/45">
              {PLAYLIST.closingLine}
            </p>

            {SPOTIFY_URL && (
              <div className="amelia-reveal mt-8 text-center">
                <a
                  href={SPOTIFY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1a2238] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#2a3654]"
                >
                  <Play className="h-4 w-4 fill-current" /> Open the playlist in Spotify
                </a>
              </div>
            )}
          </div>
        </section>

        {/* ── 11. A LITTLE MORE ABOUT THE HOUSE ── */}
        <section className="bg-[#efe7d8] py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-5">
            <div className="amelia-reveal grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-center">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#c0623a]">A little more about the house</p>
                <h2 className="mt-4 font-serif text-4xl leading-[.95] text-[#1a2238] sm:text-5xl">
                  Old bones. Good living.
                </h2>
                <p className="mt-5 max-w-md text-[15px] leading-relaxed text-[#1a2238]/70">
                  The Amelia Payson House combines the character of a historic Salem home with the comforts of a modern stay.
                </p>
              </div>
              <ul className="space-y-3">
                {AMENITIES.map((a, i) => (
                  <li key={i} className="flex items-center gap-3 rounded-xl border border-[#1a2238]/10 bg-[#f7f1e8] px-5 py-3.5">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#c0623a]/10 text-[#c0623a]">
                      <ChevronRight className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm font-medium text-[#1a2238]/80">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── 12. BEFORE YOU GO ── */}
        <section className="bg-[#1a2238] py-20 lg:py-28 text-[#f7f1e8]">
          <div className="mx-auto max-w-3xl px-5 text-center">
            <div className="amelia-reveal">
              <h2 className="font-serif text-3xl leading-[1.05] text-white sm:text-4xl">
                You’re staying in someone else’s 1845 dream house.
              </h2>
              <div className="mt-6 space-y-3 text-[15px] leading-relaxed text-[#f7f1e8]/70">
                <p>Edward and Amelia built this house nearly two centuries ago.</p>
                <p>Today it gets to do something they probably never imagined:</p>
                <p className="font-serif text-xl text-[#c0623a]">welcome people from all over the world to Salem.</p>
              </div>
              <p className="mt-6 text-[15px] leading-relaxed text-[#f7f1e8]/55">
                Enjoy the fireplace. Eat the cookies. Walk everywhere. And have another cup of coffee.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <a
                  href={HOUSE.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#c0623a] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#a8512f]"
                >
                  Explore Amelia Payson House <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href={BOND.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:border-[#c0623a] hover:text-[#c0623a]"
                >
                  Explore Bond Coffee <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── 13. FOOTER ── */}
        <footer className="bg-[#131826] py-12 text-center text-[#f7f1e8]/50">
          <div className="mx-auto max-w-3xl px-5">
            <p className="font-serif text-lg text-[#f7f1e8]/80">{HOUSE.name}</p>
            <p className="mt-1 text-sm">{HOUSE.address}</p>
            <p className="text-sm">{HOUSE.city}</p>
            <p className="mt-5 text-xs leading-relaxed">
              Coffee roasted exclusively for {HOUSE.name} by {BOND.name}.
            </p>
            <div className="mt-6 flex items-center justify-center gap-5">
              <a href={HOUSE.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-[#f7f1e8]/50 transition hover:text-[#c0623a]" aria-label="Instagram">
                <InstagramIcon />
              </a>
              <a href={HOUSE.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-[#f7f1e8]/50 transition hover:text-[#c0623a]" aria-label="Website">
                <GlobeIcon />
              </a>
              <a href={HOUSE.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="text-[#f7f1e8]/50 transition hover:text-[#c0623a]" aria-label="Google Maps">
                <MapPin className="h-5 w-5" />
              </a>
            </div>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 text-xs text-[#f7f1e8]/40 transition hover:text-[#c0623a]">
              <ArrowUpRight className="h-3.5 w-3.5" /> Bond Coffee Roasters
            </Link>
          </div>
        </footer>
      </main>

      {/* ── Floating back-to-top ── */}
      {showBackTop && (
        <button
          onClick={() => scrollTo('top')}
          className="fixed bottom-5 right-5 z-50 grid h-12 w-12 place-items-center rounded-full bg-[#1a2238] text-white shadow-lg transition hover:bg-[#c0623a] animate-fadeIn"
          aria-label="Back to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

function InstagramIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <line x1="17.5" y1="6.5" x2="17.5" y2="6.5" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15 15 0 0 1 0 20a15 15 0 0 1 0-20" />
    </svg>
  );
}
